import { Search } from 'lucide-react';
import type React from 'react';
import { usePackages } from '../../../../services/package.service';
import type { RegistrationClient, RegistrationProvider, UserFilterValue } from '../types';

export interface UserPackageOption {
  code: string;
  name: string;
}

interface UserFilterBarProps {
  value: UserFilterValue;
  onChange: (partial: Partial<UserFilterValue>) => void;
  packageOptions?: UserPackageOption[];
  trailing?: React.ReactNode;
}

const filterSelectClassName =
  'h-12 rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200';

export const UserFilterBar: React.FC<UserFilterBarProps> = ({
  value,
  onChange,
  packageOptions,
  trailing,
}) => {
  const { data: packages = [] } = usePackages();
  const planOptions = packageOptions ?? packages.map((pkg) => ({ code: pkg.code, name: pkg.name }));
  const selectedPlanMissing = value.plan
    ? !planOptions.some((pkg) => pkg.code === value.plan)
    : false;

  return (
    <div className="mb-4 flex min-w-0 flex-wrap items-center gap-3">
      <div className="relative w-full min-w-0 sm:w-80 lg:w-96">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400"
          size={20}
        />
        <input
          type="text"
          placeholder="Search by username or email..."
          value={value.search}
          onChange={(event) => onChange({ search: event.target.value })}
          className="w-full rounded-lg border border-gray-300 bg-white py-3 pl-10 pr-4 text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-slate-700 dark:bg-slate-800 dark:text-white dark:placeholder-slate-400"
        />
      </div>

      <div className="grid min-w-0 flex-1 grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:flex xl:items-center">
        <select
          aria-label="Filter by role"
          value={value.role}
          onChange={(event) => onChange({ role: event.target.value as UserFilterValue['role'] })}
          className={filterSelectClassName}
        >
          <option value="">All roles</option>
          <option value="ADMIN">Admin</option>
          <option value="USER">User</option>
        </select>

        <select
          aria-label="Filter by status"
          value={value.status}
          onChange={(event) =>
            onChange({ status: event.target.value as UserFilterValue['status'] })
          }
          className={filterSelectClassName}
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Suspended</option>
        </select>

        <select
          aria-label="Filter by plan"
          value={value.plan}
          onChange={(event) => onChange({ plan: event.target.value })}
          className={filterSelectClassName}
        >
          <option value="">All plans</option>
          {selectedPlanMissing && <option value={value.plan}>{value.plan}</option>}
          {planOptions.map((pkg) => (
            <option key={pkg.code} value={pkg.code}>
              {pkg.name} ({pkg.code})
            </option>
          ))}
        </select>

        <select
          aria-label="Filter by anonymous user type"
          value={value.anonymous}
          onChange={(event) =>
            onChange({ anonymous: event.target.value as UserFilterValue['anonymous'] })
          }
          className={filterSelectClassName}
        >
          <option value="">All user types</option>
          <option value="anonymous">Anonymous users</option>
          <option value="registered">Non-anonymous users</option>
        </select>

        <select
          aria-label="Filter by registration channel"
          value={value.registrationProvider}
          onChange={(event) =>
            onChange({ registrationProvider: event.target.value as RegistrationProvider | '' })
          }
          className={filterSelectClassName}
        >
          <option value="">All channels</option>
          <option value="EMAIL">Email</option>
          <option value="GOOGLE">Google</option>
          <option value="FACEBOOK">Facebook</option>
          <option value="APPLE">Apple</option>
          <option value="GITHUB">GitHub</option>
          <option value="ANONYMOUS">Anonymous</option>
          <option value="ADMIN_CREATED">Admin created</option>
          <option value="UNKNOWN">Unknown</option>
        </select>

        <select
          aria-label="Filter by registration client"
          value={value.registrationClient}
          onChange={(event) =>
            onChange({ registrationClient: event.target.value as RegistrationClient | '' })
          }
          className={filterSelectClassName}
        >
          <option value="">All clients</option>
          <option value="WEB">Web</option>
          <option value="IOS">iOS</option>
          <option value="ANDROID">Android</option>
          <option value="ADMIN">Admin</option>
          <option value="API">API</option>
          <option value="UNKNOWN">Unknown</option>
        </select>

        {trailing}
      </div>
    </div>
  );
};
